package pl.mothdigital.neuronowi.pages.specialists

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.foundation.layout.*
import com.varabyte.kobweb.compose.ui.*
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.core.*
import com.varabyte.kobweb.silk.components.icons.fa.*
import com.varabyte.kobweb.silk.components.layout.*
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.css.keywords.auto
import org.jetbrains.compose.web.dom.*
import pl.mothdigital.neuronowi.common.model.*
import pl.mothdigital.neuronowi.common.resources.*
import pl.mothdigital.neuronowi.common.utils.fixPolishOrphans
import pl.mothdigital.neuronowi.components.*
import pl.mothdigital.neuronowi.components.layouts.*
import pl.mothdigital.neuronowi.components.sections.cta.*
import pl.mothdigital.neuronowi.components.sections.specialist.*
import pl.mothdigital.neuronowi.components.text.*
import pl.mothdigital.neuronowi.theme.*

private val specializations = listOf(
    SpecialistData(
        icon = { FaChild() },
        title = StringResources.MARTA_DYBALSKA_SPEC_INTEGRATION_TITLE,
        description = StringResources.MARTA_DYBALSKA_SPEC_INTEGRATION_DESC
    ),
    SpecialistData(
        icon = { FaFaceSmile() },
        title = StringResources.MARTA_DYBALSKA_SPEC_EMOTIONS_TITLE,
        description = StringResources.MARTA_DYBALSKA_SPEC_EMOTIONS_DESC
    ),
    SpecialistData(
        icon = { FaPersonWalking() },
        title = StringResources.MARTA_DYBALSKA_SPEC_CONCENTRATION_TITLE,
        description = StringResources.MARTA_DYBALSKA_SPEC_CONCENTRATION_DESC
    ),
    SpecialistData(
        icon = { FaBrain() },
        title = StringResources.MARTA_DYBALSKA_SPEC_PSYCHOLOGY_TITLE,
        description = StringResources.MARTA_DYBALSKA_SPEC_PSYCHOLOGY_DESC
    )
)

private val services = listOf(
    StringResources.MARTA_DYBALSKA_SERVICE_1,
    StringResources.MARTA_DYBALSKA_SERVICE_2,
    StringResources.MARTA_DYBALSKA_SERVICE_3,
    StringResources.MARTA_DYBALSKA_SERVICE_4,
    StringResources.MARTA_DYBALSKA_SERVICE_5
)

private val priceItems = listOf(
    PriceItem(
        StringResources.MARTA_DYBALSKA_PRICE_1_NAME,
        StringResources.MARTA_DYBALSKA_PRICE_1_TIME,
        StringResources.MARTA_DYBALSKA_PRICE_1_AMOUNT
    ),
    PriceItem(
        StringResources.MARTA_DYBALSKA_PRICE_2_NAME,
        StringResources.MARTA_DYBALSKA_PRICE_2_TIME,
        StringResources.MARTA_DYBALSKA_PRICE_2_AMOUNT
    )
)

@Page
@Composable
fun MartaDybalskaPage() {
    SpecialistPageLayout(
        title = StringResources.MARTA_DYBALSKA_PAGE_TITLE,
        headerSection = { HeaderSection() },
        profileSection = { ProfileSection() },
        specializationsSection = { SpecializationsSection() },
        servicesSection = { ServicesSection() },
        priceSection = { PriceSection() },
        ctaSection = { context ->
            CTASection(
                ctx = context,
                customAction = { js("window.scrollTo({ top: 0, behavior: 'smooth' })") },
            )
        }
    )
}

@Composable
private fun HeaderSection() {
    SpecialistHeaderSection(
        name = StringResources.MARTA_DYBALSKA,
        titles = StringResources.MARTA_DYBALSKA_ACADEMIC_TITLES,
        phoneNumber = StringResources.MARTA_DYBALSKA_PHONE_NUMBER
    )
}

@Composable
private fun ProfileSection() {
    SpecialistProfileSection(
        avatar = ImageResources.avatarMartaDybalska,
        aboutParagraphs = listOf(
            StringResources.MARTA_DYBALSKA_ABOUT_ME_1,
            StringResources.MARTA_DYBALSKA_ABOUT_ME_2,
            StringResources.MARTA_DYBALSKA_ABOUT_ME_3,
            StringResources.MARTA_DYBALSKA_ABOUT_ME_4,
            StringResources.MARTA_DYBALSKA_ABOUT_ME_5,
            StringResources.MARTA_DYBALSKA_ABOUT_ME_6
        ),
        inviteMessage = StringResources.MARTA_DYBALSKA_ABOUT_ME_INVITE,
        imagePosition = ImagePosition.LEFT
    )
}

@Composable
private fun SpecializationsSection() {
    SpecializationsGridSection(
        specializations = specializations
    )
}

@Composable
private fun ServicesSection() {
    Section(
        attrs = Modifier
            .fillMaxWidth()
            .padding(topBottom = 3.cssRem, leftRight = 6.percent)
            .toAttrs()
    ) {
        Column(modifier = Modifier.fillMaxWidth().margin { auto }.gap(2.cssRem)) {
            AppH2 { Text(StringResources.MARTA_DYBALSKA_SERVICES_TITLE.fixPolishOrphans()) }

            SimpleGrid(
                modifier = Modifier.fillMaxWidth().gap(1.cssRem),
                numColumns = numColumns(base = 1, sm = 1, md = 2, lg = 2, xl = 2)
            ) {
                Column(modifier = Modifier.gap(0.5.cssRem)) {
                    Ul {
                        services.forEach { Li { Text(it.fixPolishOrphans()) } }
                    }
                }
            }
        }
    }
}

@Composable
private fun PriceSection() {
    Section(
        attrs = Modifier
            .fillMaxWidth()
            .padding(topBottom = 3.cssRem, leftRight = 6.percent)
            .backgroundColor(SiteTheme.green._50.toRgb().copy(alpha = 128))
            .toAttrs()
    ) {
        Column(modifier = Modifier.fillMaxWidth().margin { auto }.gap(2.cssRem)) {
            AppH2 { Text(StringResources.MARTA_DYBALSKA_PRICE_TITLE.fixPolishOrphans()) }

            Column(modifier = Modifier.gap(0.5.cssRem)) {
                AppP(modifier = Modifier.fontSize(0.9.cssRem).margin(bottom = 0.px)) {
                    Text(StringResources.MARTA_DYBALSKA_PRICE_HEADER_1.fixPolishOrphans())
                }
                AppP(modifier = Modifier.fontSize(0.9.cssRem).margin(bottom = 1.cssRem)) {
                    Text(StringResources.MARTA_DYBALSKA_PRICE_HEADER_2.fixPolishOrphans())
                }
            }

            SpecialistPriceTable(priceItems = priceItems)

            AppP(modifier = Modifier.fontSize(0.9.cssRem), color = SiteTheme.black._700) {
                Text(StringResources.MARTA_DYBALSKA_PRICE_NOTE.fixPolishOrphans())
            }
        }
    }
}
